import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useSyncStatus } from '@/components/plaid/helpers'
import {
  resolveSyncErrorClassification,
  type ResolvedPlaidItemSyncStatus,
} from './bankAccounts.helpers'

interface SyncStatusContextValue {
  plaidItems: ResolvedPlaidItemSyncStatus[]
  /** Restart polling — call after a manual retry/reconnect so the UI reflects
   * the new sync state (polling self-stops once every item is terminal). */
  refetch: () => Promise<void>
}

const SyncStatusContext = createContext<SyncStatusContextValue | null>(null)

/**
 * Shares a single {@link useSyncStatus} poller across the self-serve bank settings —
 * both the page-level {@link FinancialInstitutionsError} banner (above the tabs) and
 * the per-account error indicators inside the Financial Institutions tab read from it,
 * so there is one poller and one `refetch`.
 */
export const SyncStatusProvider = ({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) => {
  const { plaidItems, refetch } = useSyncStatus({ enabled })

  const items = useMemo<ResolvedPlaidItemSyncStatus[]>(
    () =>
      plaidItems.map((item) => ({
        ...item,
        resolvedErrorClassification: resolveSyncErrorClassification(item),
      })),
    [plaidItems]
  )

  return (
    <SyncStatusContext.Provider value={{ plaidItems: items, refetch }}>
      {children}
    </SyncStatusContext.Provider>
  )
}

export const useSyncStatusContext = (): SyncStatusContextValue => {
  const context = useContext(SyncStatusContext)
  if (!context) {
    throw new Error(
      'useSyncStatusContext must be used within a SyncStatusProvider'
    )
  }
  return context
}
